import { getIntegrationBrandIcon } from "@client-app/lib/integration-brand-icons";
import type {
  InviteProjectAccessGrant,
  ManageableApplicationResource,
} from "@common/api/featureService";

import type {
  BrandConnectionAccessLevel,
  BrandConnection,
  BrandPrototypeData,
} from "./brandPrototypeModel";
import type {
  BrandConnection as ProjectConnection,
  BrandGroupData,
  BrandResource,
} from "./brandGroupViewModel";
import type { ProjectResourceUiAccess } from "./projectResourceAccess";

export type AssignableBrandConnection = BrandConnection & {
  integrationConnectionId?: string;
  assignmentKey: string;
  assignmentScope: "connection" | "google-ads-customer" | "resource";
  resourceType?: "CONNECTION" | "CUSTOMER" | "PROPERTY" | "SITE";
  resourceKey?: string;
  authorizedAccountName?: string;
  /** Maximum access delegated by the assignment for each Project. */
  brandAccessCeilings?: Record<string, "DISABLED" | "READ" | "WRITE">;
  resource?: BrandResource;
  /** Readable owner identity for account-selection surfaces. */
  connectedByDisplayName?: string;
};

const PROJECT_CONNECTION_ACCESS_LEVELS: BrandConnectionAccessLevel[] = [
  "no_access",
  "read",
  "action",
];

const normalizeCurrentUserId = (value?: string | null) =>
  String(value || "").trim().toLowerCase();

/**
 * A connection's OAuth owner must keep WRITE access. Ownership is determined
 * only from the selected target user's identity. `canManageAccess` describes
 * the current viewer, so using it here would incorrectly downgrade a selected
 * connection owner whenever the Project manager cannot use that application.
 * The server remains authoritative for active ownership and manage-access.
 */
export const isProjectConnectionOwner = (
  connection: Pick<AssignableBrandConnection, "connectedByUserId">,
  userId?: string,
) => {
  const connectionOwnerId = String(connection.connectedByUserId || "").trim();
  const targetUserId = String(userId || "").trim();

  return Boolean(
    connectionOwnerId &&
      targetUserId &&
      connectionOwnerId === targetUserId,
  );
};

export const getProjectConnectionAccessLevels = (
  connection: Pick<
    AssignableBrandConnection,
    "connectedByUserId" | "canManageAccess" | "brandAccessCeilings"
  >,
  projectId?: string,
  userId?: string,
): BrandConnectionAccessLevel[] => {
  if (isProjectConnectionOwner(connection, userId)) {
    return PROJECT_CONNECTION_ACCESS_LEVELS;
  }
  const ceiling = projectId
    ? connection.brandAccessCeilings?.[projectId]
    : undefined;
  if (ceiling === "DISABLED") return ["no_access"];
  if (ceiling === "READ") return ["no_access", "read"];
  return PROJECT_CONNECTION_ACCESS_LEVELS;
};

/**
 * Delegation is bounded by both the Project assignment ceiling and the
 * actor's effective access. A connection owner may retain action access for
 * their own runtime use, but that exception must never raise what they can
 * delegate to another user above the persisted assignment ceiling.
 */
export const getDelegatableProjectConnectionAccessLevels = (
  connection: Pick<
    AssignableBrandConnection,
    "connectedByUserId" | "brandAccessCeilings"
  >,
  projectId: string | undefined,
  actorAccess: ProjectResourceUiAccess,
  targetUserId?: string,
): BrandConnectionAccessLevel[] => {
  if (isProjectConnectionOwner(connection, targetUserId)) {
    return PROJECT_CONNECTION_ACCESS_LEVELS;
  }

  const assignmentLevels = getProjectConnectionAccessLevels(
    connection,
    projectId,
    targetUserId,
  );
  const actorLevels: BrandConnectionAccessLevel[] =
    actorAccess === "action"
      ? PROJECT_CONNECTION_ACCESS_LEVELS
      : actorAccess === "read"
        ? ["no_access", "read"]
        : ["no_access"];

  return assignmentLevels.filter((level) => actorLevels.includes(level));
};

export const normalizeDelegatableProjectConnectionAccessLevel = (
  connection: Pick<
    AssignableBrandConnection,
    "connectedByUserId" | "brandAccessCeilings"
  >,
  projectId: string | undefined,
  actorAccess: ProjectResourceUiAccess,
  targetUserId: string | undefined,
  accessLevel: BrandConnectionAccessLevel,
): BrandConnectionAccessLevel => {
  if (isProjectConnectionOwner(connection, targetUserId)) {
    return "action";
  }

  const available = getDelegatableProjectConnectionAccessLevels(
    connection,
    projectId,
    actorAccess,
    targetUserId,
  );
  if (available.includes(accessLevel)) return accessLevel;
  return available.includes("read") ? "read" : "no_access";
};

export const normalizeProjectConnectionAccessLevel = (
  connection: Pick<
    AssignableBrandConnection,
    "connectedByUserId" | "canManageAccess" | "brandAccessCeilings"
  >,
  projectId: string | undefined,
  userId: string | undefined,
  accessLevel: BrandConnectionAccessLevel,
): BrandConnectionAccessLevel => {
  if (isProjectConnectionOwner(connection, userId)) {
    return "action";
  }

  const available = getProjectConnectionAccessLevels(connection, projectId, userId);
  if (available.includes(accessLevel)) return accessLevel;
  return available.includes("read") ? "read" : "no_access";
};

/**
 * Builds invite-time access rows from the API-backed Project model.
 *
 * The legacy builder below is retained for older settings surfaces, but the
 * invite modal must use BrandGroupData so Projects and integrations are read
 * from the same source as Project settings. In particular, integrations with
 * no resources are still valid connection-level rows and must remain visible.
 */
export const buildAssignableProjectConnections = (
  data: BrandGroupData,
): AssignableBrandConnection[] =>
  data.connections
    .filter((connection) => connection.status === "connected")
    .flatMap((connection: ProjectConnection) =>
      connection.brandIds.map((brandId) => {
        const application = getIntegrationBrandIcon(connection.provider);
        return {
          ...connection,
          brandId,
          provider: application.name,
          assignmentKey: `${brandId}:${connection.id}`,
          assignmentScope: "connection" as const,
          integrationConnectionId: connection.id,
        };
      }),
    );

const SECONDARY_RESOURCE_TYPE_BY_PROVIDER: Record<
  string,
  "CUSTOMER" | "PROPERTY" | "SITE"
> = {
  "Google Ads": "CUSTOMER",
  "Google Analytics": "PROPERTY",
  "Google Search Console": "SITE",
};

const selectProjectCeilings = (
  ceilings: ProjectConnection["brandAccessCeilings"],
  projectIds: string[],
) =>
  Object.fromEntries(
    projectIds.flatMap((projectId) =>
      ceilings?.[projectId] ? [[projectId, ceilings[projectId]]] : [],
    ),
  );

/**
 * Builds the canonical Project permission matrix used for existing members.
 * Only Ads CUSTOMER, Analytics PROPERTY and Search Console SITE are child
 * authorization identities. Every other provider is represented once by its
 * CONNECTION, regardless of inventory rows such as Shopify SHOP.
 */
export const buildProjectAuthorizationConnections = (
  data: BrandGroupData,
): AssignableBrandConnection[] =>
  data.connections
    .filter((connection) => connection.status === "connected")
    .flatMap((connection) => {
      const application = getIntegrationBrandIcon(connection.provider);
      const secondaryType = SECONDARY_RESOURCE_TYPE_BY_PROVIDER[application.name];
      const makeConnectionRow = (
        projectIds: string[],
      ): AssignableBrandConnection => ({
        ...connection,
        brandId: projectIds[0] || null,
        brandIds: projectIds,
        brandAccessCeilings: selectProjectCeilings(
          connection.brandAccessCeilings,
          projectIds,
        ),
        provider: application.name,
        integrationConnectionId: connection.id,
        assignmentKey: `${connection.id}:CONNECTION:${connection.id}`,
        assignmentScope: "connection",
        resourceType: "CONNECTION",
        resourceKey: connection.id,
        resource: undefined,
      });

      if (!secondaryType) {
        return [makeConnectionRow(connection.brandIds)];
      }

      const resources = connection.resources.filter(
        (resource) =>
          String(resource.status || "").toUpperCase() === "ACTIVE" &&
          String(resource.resourceType || "").toUpperCase() === secondaryType,
      );
      const assignedChildProjectIds = new Set(
        resources.flatMap((resource) => resource.brandIds),
      );
      const resourceRows = resources.map<AssignableBrandConnection>((resource) => {
        const resourceKey = String(resource.resourceKey);
        const id = `${connection.id}:${secondaryType}:${resourceKey}`;
        return {
          ...connection,
          id,
          integrationConnectionId: connection.id,
          assignmentKey: id,
          assignmentScope:
            secondaryType === "CUSTOMER" ? "google-ads-customer" : "resource",
          resourceType: secondaryType,
          resourceKey,
          provider: application.name,
          accountName: resource.name || `${application.name} ${resourceKey}`,
          accountDetail: resourceKey,
          authorizedAccountName:
            secondaryType === "CUSTOMER"
              ? connection.accountName || connection.accountDetail || undefined
              : undefined,
          brandId: resource.brandIds[0] || null,
          brandIds: resource.brandIds,
          brandAccessCeilings: resource.brandAccessCeilings,
          connectedByUserId: String(
            resource.connectedBy || connection.connectedByUserId || "",
          ),
          resource,
        };
      });
      const fallbackProjectIds = connection.brandIds.filter(
        (projectId) => !assignedChildProjectIds.has(projectId),
      );

      return fallbackProjectIds.length > 0
        ? [...resourceRows, makeConnectionRow(fallbackProjectIds)]
        : resourceRows;
    });

type ProjectAssignmentCeiling = "DISABLED" | "READ" | "WRITE";

const getMoreRestrictiveCeiling = (
  left: ProjectAssignmentCeiling | undefined,
  right: ProjectAssignmentCeiling,
): ProjectAssignmentCeiling => {
  if (!left) return right;
  const rank: Record<ProjectAssignmentCeiling, number> = {
    DISABLED: 0,
    READ: 1,
    WRITE: 2,
  };
  return rank[left] <= rank[right] ? left : right;
};

/**
 * Builds the inviter's application inventory without coupling it to Project
 * ownership. Duplicate API rows are folded into one connection and conflicting
 * assignment ceilings fail closed to the more restrictive value.
 */
export const buildInviteOwnedApplicationConnections = (
  data: BrandGroupData,
  currentUserIds: string[],
): AssignableBrandConnection[] => {
  const currentUserIdSet = new Set(
    currentUserIds.map(normalizeCurrentUserId).filter(Boolean),
  );
  const publicProjectIds = data.brands
    .filter(
      (brand) =>
        String(brand.visibility || brand.scope || "WORKSPACE").toUpperCase() !==
        "PRIVATE",
    )
    .map((brand) => brand.id);
  const publicProjectIdSet = new Set(publicProjectIds);
  const aggregated = new Map<
    string,
    {
      connection: ProjectConnection;
      assignedProjectIds: Set<string>;
      ceilings: Record<string, ProjectAssignmentCeiling>;
    }
  >();

  data.connections.forEach((connection) => {
    if (
      connection.status !== "connected" ||
      !currentUserIdSet.has(normalizeCurrentUserId(connection.connectedByUserId))
    ) {
      return;
    }

    const entry = aggregated.get(connection.id) || {
      connection,
      assignedProjectIds: new Set<string>(),
      ceilings: {},
    };
    connection.brandIds.forEach((projectId) => {
      if (!publicProjectIdSet.has(projectId)) return;
      const ceiling = connection.brandAccessCeilings?.[projectId];
      if (!ceiling) return;
      entry.assignedProjectIds.add(projectId);
      entry.ceilings[projectId] = getMoreRestrictiveCeiling(
        entry.ceilings[projectId],
        ceiling,
      );
    });
    aggregated.set(connection.id, entry);
  });

  return [...aggregated.values()].map(({ connection, assignedProjectIds, ceilings }) => {
    const safeProjectIds = publicProjectIds.filter(
      (projectId) =>
        assignedProjectIds.has(projectId) &&
        (ceilings[projectId] === "READ" || ceilings[projectId] === "WRITE"),
    );
    const application = getIntegrationBrandIcon(connection.provider);

    return {
      ...connection,
      brandId: null,
      brandIds: safeProjectIds,
      brandAccessCeilings: ceilings,
      provider: application.name,
      assignmentKey: `connection:${connection.id}`,
      assignmentScope: "connection" as const,
      integrationConnectionId: connection.id,
    };
  });
};

/** Maps the function-backed canonical authorization contract to invite rows. */
export const buildInviteManageableApplicationResources = (
  resources: ManageableApplicationResource[],
): AssignableBrandConnection[] => {
  const rows = new Map<string, AssignableBrandConnection>();
  resources.forEach((resource) => {
    if (
      !resource ||
      !resource.canManageAccess ||
      !resource.resourceType ||
      !resource.resourceKey
    ) {
      return;
    }
    const connectionId = String(resource.connectionId);
    const resourceType = resource.resourceType.toUpperCase() as
      AssignableBrandConnection["resourceType"];
    const resourceKey = String(resource.resourceKey);
    const assignmentKey = `${connectionId}:${resourceType}:${resourceKey}`;
    const projectAccess = Array.isArray(resource.projectAccess)
      ? resource.projectAccess
      : [];
    const brandIds = projectAccess.map((project) => project.projectId);
    const brandAccessCeilings = Object.fromEntries(
      projectAccess.map((project) => [
        project.projectId,
        project.maxAccessLevel,
      ]),
    );
    const application = getIntegrationBrandIcon(resource.provider);
    const existing = rows.get(assignmentKey);
    if (existing) {
      existing.brandIds = Array.from(new Set([...existing.brandIds, ...brandIds]));
      const mergedCeilings: Record<string, ProjectAssignmentCeiling> = {
        ...existing.brandAccessCeilings,
      };
      Object.entries(brandAccessCeilings).forEach(([projectId, ceiling]) => {
        mergedCeilings[projectId] = getMoreRestrictiveCeiling(
          mergedCeilings[projectId],
          ceiling,
        );
      });
      existing.brandAccessCeilings = mergedCeilings;
      return;
    }
    rows.set(assignmentKey, {
      id: assignmentKey,
      provider: application.name,
      accountName: resource.resourceName || resource.connectionName,
      accountDetail: resource.connectionName || resource.resourceName,
      brandId: null,
      brandIds,
      brandAccessCeilings,
      status: "connected",
      connectedByUserId: resource.connectedBy,
      connectedAt: "",
      assignedByUserId: null,
      assignedAt: null,
      canUseConnection: true,
      canManageConnection: true,
      canManageAccess: resource.canManageAccess,
      assignmentKey,
      assignmentScope:
        resourceType === "CONNECTION" ? "connection" : "resource",
      integrationConnectionId: connectionId,
      resourceType,
      resourceKey,
    });
  });
  return [...rows.values()];
};

export type InviteApplicationResourceDisplayContext = {
  projectData: Pick<BrandGroupData, "connections" | "users">;
};

/**
 * Enrich canonical invitation resources with display metadata. Delegation
 * ceilings remain the exact values returned by the manageable-resource
 * contract; the connection Owner's runtime access is not a delegation ceiling.
 */
export const enrichInviteApplicationResources = (
  connections: AssignableBrandConnection[],
  { projectData }: InviteApplicationResourceDisplayContext,
): AssignableBrandConnection[] =>
  connections.map((connection) => {
    const connectionId = String(connection.integrationConnectionId || connection.id);
    const resourceType = connection.resourceType || "CONNECTION";
    const resourceKey = connection.resourceKey || connectionId;
    const sourceConnection = projectData.connections.find(
      (candidate) => String(candidate.id) === connectionId,
    );
    const sourceResource = sourceConnection?.resources.find(
      (candidate) =>
        String(candidate.resourceType).toUpperCase() === resourceType &&
        String(candidate.resourceKey) === resourceKey,
    );
    const isResourceScoped = resourceType !== "CONNECTION";
    const accountName = isResourceScoped
      ? sourceResource?.name || sourceResource?.resourceKey || connection.accountName
      : sourceConnection?.accountName || sourceConnection?.accountDetail || connection.accountName;
    const accountDetail = isResourceScoped
      ? sourceResource?.resourceKey || connection.accountDetail
      : sourceConnection?.accountDetail || connection.accountDetail;
    const connectedByUserId =
      sourceConnection?.connectedByUserId || connection.connectedByUserId;
    const connectionOwner = (projectData.users || []).find(
      (user) =>
        normalizeCurrentUserId(user.id) ===
        normalizeCurrentUserId(connectedByUserId),
    );

    return {
      ...connection,
      accountName,
      accountDetail,
      connectedByUserId,
      connectedAt: sourceConnection?.connectedAt || connection.connectedAt,
      connectedByDisplayName:
        connectionOwner?.name || connectionOwner?.email || connectedByUserId,
    };
  });

export const getInviteProjectAccessDraftKey = (
  projectId: string,
  assignmentKey: string,
) => `project:${projectId}|resource:${assignmentKey}`;

/** Expands per-Project application choices into independent invitation grants. */
export const buildInviteProjectAccessGrants = (
  connections: AssignableBrandConnection[],
  selectedProjectIds: string[],
  accessByProjectConnectionKey: Record<
    string,
    BrandConnectionAccessLevel | undefined
  >,
): InviteProjectAccessGrant[] => {
  const selectedProjectIdSet = new Set(selectedProjectIds);
  const identities = new Set<string>();
  const grants: InviteProjectAccessGrant[] = [];

  connections.forEach((connection) => {
    connection.brandIds.forEach((projectId) => {
      if (!selectedProjectIdSet.has(projectId)) return;
      const requestedAccess =
        accessByProjectConnectionKey[
          getInviteProjectAccessDraftKey(projectId, connection.assignmentKey)
        ] || "no_access";
      const accessLevel = normalizeProjectConnectionAccessLevel(
        connection,
        projectId,
        undefined,
        requestedAccess,
      );
      const connectionId = String(connection.integrationConnectionId || connection.id);
      const resourceType = connection.resourceType || "CONNECTION";
      const resourceKey = connection.resourceKey || connectionId;
      const identity = `${projectId}:${connectionId}:${resourceType}:${resourceKey}`;
      if (identities.has(identity)) return;
      identities.add(identity);
      grants.push({
        projectId,
        connectionId,
        resourceType,
        resourceKey,
        accessLevel:
          accessLevel === "action"
            ? "WRITE"
            : accessLevel === "read"
              ? "READ"
              : "DISABLED",
      });
    });
  });

  return grants;
};

export type ExistingConnectionPickerGroup = {
  id: string;
  kind: "connection" | "google-ads" | "secondary-resources";
  provider: string;
  authorizedAccountName?: string;
  connections: AssignableBrandConnection[];
  connectionIds: string[];
};

const getPickerGroupId = (connection: AssignableBrandConnection) =>
  String(connection.resource?.connectionId ?? connection.id.split(":")[0]);

const isConnectedByCurrentUser = (
  connection: Pick<AssignableBrandConnection, "connectedByUserId">,
  currentUserIds: string[] = [],
) => {
  const ownerId = normalizeCurrentUserId(connection.connectedByUserId);
  if (!ownerId) return false;
  return currentUserIds
    .map(normalizeCurrentUserId)
    .filter(Boolean)
    .includes(ownerId);
};

/**
 * The add-existing picker is an application-owner delegation action. It must
 * only expose resources connected by the current user; Business-group ownership
 * alone is not enough to attach someone else's application to a Project.
 */
export const canAddExistingConnectionToBusinessGroup = (
  connection: AssignableBrandConnection,
  currentUserIds: string[] = [],
) =>
  connection.resource?.isInResourceContract !== false &&
  isConnectedByCurrentUser(connection, currentUserIds);

/**
 * Secondary-resource providers share one OAuth connection. Keep that
 * relationship visible in the picker, while selected values remain exact
 * resource-level IDs so Project authorization cannot include siblings.
 */
export const getExistingConnectionPickerGroups = (
  connections: AssignableBrandConnection[],
  activeBrandId: string,
  currentUserIds: string[] = [],
  _isWorkspaceOwner = false,
): ExistingConnectionPickerGroup[] => {
  const groups = new Map<string, ExistingConnectionPickerGroup>();

  connections
    .filter((connection) => !connection.brandIds.includes(activeBrandId))
    .filter((connection) =>
      canAddExistingConnectionToBusinessGroup(connection, currentUserIds),
    )
    .forEach((connection) => {
      const isGoogleAds = connection.assignmentScope === "google-ads-customer";
      const isSecondaryResource = isGoogleAds || ["CUSTOMER", "PROPERTY", "SITE"].includes(
        String(connection.resource?.resourceType || "").toUpperCase(),
      );
      const id = isSecondaryResource
        ? `resource:${getPickerGroupId(connection)}`
        : `connection:${connection.id}`;
      const existing = groups.get(id);

      if (existing) {
        existing.connections.push(connection);
        existing.connectionIds.push(connection.id);
        return;
      }

      groups.set(id, {
        id,
        kind: isGoogleAds ? "google-ads" : isSecondaryResource ? "secondary-resources" : "connection",
        provider: connection.provider,
        authorizedAccountName: connection.authorizedAccountName,
        connections: [connection],
        connectionIds: [connection.id],
      });
    });

  return [...groups.values()].sort((left, right) =>
    left.provider.localeCompare(right.provider, undefined, {
      sensitivity: "base",
    }),
  );
};

export const toggleExistingConnectionPickerGroup = (
  currentIds: string[],
  groupConnectionIds: string[],
): string[] => {
  const selectedIds = new Set(currentIds);
  const isSelected = groupConnectionIds.every((id) => selectedIds.has(id));

  groupConnectionIds.forEach((id) => {
    if (isSelected) {
      selectedIds.delete(id);
      return;
    }
    selectedIds.add(id);
  });

  return [...selectedIds];
};

const getWorkspaceConnectionAccount = (
  profile: Record<string, unknown>,
  mapping: Record<string, unknown> | undefined,
  provider: string,
) =>
  String(
    mapping?.shop_domain ||
      mapping?.shopDomain ||
      mapping?.store_url ||
      mapping?.storeUrl ||
      mapping?.authorized_account_name ||
      mapping?.authorizedAccountName ||
      mapping?.external_account_name ||
      mapping?.externalAccountName ||
      mapping?.account_name ||
      mapping?.account_id ||
      profile.shop_domain ||
      profile.shopDomain ||
      profile.accountId ||
      profile.accountName ||
      provider,
  );

const getWorkspaceConnectionName = (
  profile: Record<string, unknown>,
  mapping: Record<string, unknown> | undefined,
  provider: string,
) =>
  String(
    mapping?.name ||
      mapping?.store_name ||
      mapping?.shop_name ||
      mapping?.account_name ||
      profile.connection_name ||
      profile.accountName ||
      provider,
  );

export const getGoogleAdsCustomerBrandKey = (connectionId: string, customerId: string) =>
  `${connectionId}:${customerId}`;

/**
 * Builds the single account-level connection model shared by Brand access and
 * member invitation. Keeping this mapping in one place prevents the two entry
 * points from assigning permissions to different connection identifiers.
 */
export const buildAssignableBrandConnections = ({
  data,
  integrations,
}: {
  data: BrandPrototypeData;
  integrations: Array<Record<string, any>>;
}): AssignableBrandConnection[] => {
  const connectedWorkspaceAccounts = integrations.flatMap<AssignableBrandConnection>((integration) => {
    if (!integration.connected) {
      return [];
    }

    const profile = (integration.profile_data || {}) as Record<string, unknown>;
    const integrationMappings = Array.isArray(integration.mappings) ? integration.mappings : [];
    const mappings: Array<Record<string, any> | undefined> = integrationMappings.length
      ? integrationMappings
      : [undefined];
    const application = getIntegrationBrandIcon(String(integration.provider || ""));
    const isGoogleAdsConnection = application.name === "Google Ads";

    return mappings.flatMap<AssignableBrandConnection>((mapping, index) => {
      const id = String(mapping?.id || `${integration.provider}-${index}`);

      if (isGoogleAdsConnection) {
        const subAccounts = Array.isArray(mapping?.sub_accounts) ? mapping.sub_accounts : [];

        return subAccounts.flatMap<AssignableBrandConnection>((subAccount: Record<string, unknown>) => {
          const customerId = String(subAccount.customer_id || subAccount.account_id || "");
          if (!customerId) {
            return [];
          }

          const assignmentKey = getGoogleAdsCustomerBrandKey(id, customerId);
          const brandIds = data.externalGoogleAdsCustomerBrandIds[assignmentKey] || [];

          return [
            {
              id: assignmentKey,
              assignmentKey,
              assignmentScope: "google-ads-customer",
              provider: application.name,
              accountName: String(subAccount.account_name || customerId),
              accountDetail: customerId,
              authorizedAccountName: getWorkspaceConnectionAccount(profile, mapping, application.name),
              brandId: brandIds[0] || null,
              brandIds,
              status: "connected",
              connectedByUserId: String(mapping?.user_id || ""),
              connectedAt: String(mapping?.created_at || integration.connectedAt || integration.created_at || ""),
              assignedByUserId: null,
              assignedAt: null,
            },
          ];
        });
      }

      const brandIds = data.externalConnectionBrandIds[id] || [];
      return [
        {
          id,
          assignmentKey: id,
          assignmentScope: "connection",
          provider: application.name,
          accountName: getWorkspaceConnectionName(profile, mapping, application.name),
          accountDetail: getWorkspaceConnectionAccount(profile, mapping, application.name),
          brandId: brandIds[0] || null,
          brandIds,
          status: "connected",
          connectedByUserId: String(mapping?.user_id || ""),
          connectedAt: String(mapping?.created_at || integration.connectedAt || integration.created_at || ""),
          assignedByUserId: null,
          assignedAt: null,
        },
      ];
    });
  });

  return connectedWorkspaceAccounts;
};
